// @ts-check
import * as R from "ramda";
import qs from "qs";
import { INVOICING_SERVICE_URI } from "env";
import { assertOk, proxyFetch } from "../utils";

export { default as usePayInvoice } from "./usePayInvoice";
export { default as useGetAuthorizedInvoiceById } from "./useGetAuthorizedInvoiceById";
export { default as useGetDepositCalculations } from "./useGetDepositCalculations";
export { default as useIntentDeposit } from "./useIntentDeposit";
export { default as useGetPayoutCalculations } from "./useGetPayoutCalculations";
export { default as useCreatePayoutRequest } from "./useCreatePayoutRequest";
export { default as useGetPendingPayouts } from "./useGetPendingPayouts";
export { default as useCancelPayoutRequest } from "./useCancelPayoutRequest";

const invoicingProxy = proxyFetch(INVOICING_SERVICE_URI);
const sortBySortOrder = R.sortBy(R.prop("sort_order"));

export const TRANSACTION_HISTORY_LIMIT = 20;
const transactionQueryParams = {
  accountId: "account_id",
  limit: "limit",
  offset: "offset",
  statuses: "statuses",
  transactionTypes: "transaction_types",
};

// - - - - - - -
// MUTATIONS
export const payInvoiceWithInvoiceId = async (ctx, invoiceId, isEscrow = false) => {
  if (!invoiceId) {
    throw new Error("Invoice ID missing when attempting to pay for invoice");
  }

  const endpoint = `/v2/${isEscrow ? "escrow" : "invoice"}/${invoiceId}/pay`;
  const response = await invoicingProxy(ctx, endpoint, "POST", {
    session_id: ctx.sessionId,
  });
  assertOk(response);
  return response.json();
};

export const createInvoiceWithAuthorizedInvoiceId = async (
  ctx,
  authorizedInvoiceId,
  paymentMethods,
  isEscrow = false
) => {
  if (!authorizedInvoiceId) {
    throw new Error("Authorized invoice ID missing when attempting to create an invoice");
  }
  if (!paymentMethods) {
    throw new Error("Payment methods missing when attempting to create an invoice");
  }

  const endpoint = `/v2/${isEscrow ? "escrow" : "invoice"}`;
  const response = await invoicingProxy(ctx, endpoint, "POST", {
    authorized_invoice_id: authorizedInvoiceId,
    payment_methods: paymentMethods,
  });

  // This endpoint returns the 'line_items' key as an object, but the one below
  // returns it as an array of objects. This endpoint also places the total amount
  // and currency in a sub-key, but the one below does not. On top of this, if the
  // invoice is escrow, all root keys from the other two endpoints are placed on a
  // 'escrow_invoice' sub-key. The cherry on top of all of this is that the ID that
  // /escrow/[id]/pay takes in, sits on the root of the payload coming back from
  // /escrow, not within the escrow_payload sub-key, but it sits within the payload
  // itself on /invoice, so we also need to grab that ID seperately, if that's the case.
  const data = await response.json();
  if (!data.payload) {
    throw new Error("Invalid response when creating an invoice");
  }

  assertOk(response, [], data);

  return new Promise((resolve) => {
    let payload = data.payload;
    if (isEscrow) {
      payload = data.payload.escrow_invoice || {};
    }

    // TODO: Perhaps we use immer for stuff like this?
    const sortedLineItemsValues = sortBySortOrder(Object.values(payload.line_items ?? {}));
    const massagedPayload = {
      ...payload,
      line_items: sortedLineItemsValues,
      display_amount: payload.summary?.display_amount,
      line_items_currency: payload.summary?.currency,
      line_items_total: payload.summary?.total_amount,
    };

    if (isEscrow) {
      massagedPayload["escrow_id"] = data.payload.id;
    }

    const massagedData = { ...data, payload: massagedPayload };
    return resolve(massagedData);
  });
};

// - - - - - - -
// QUERIES
export const getLineItemsWithAuthorizedInvoiceId = async (ctx, authorizedInvoiceId) => {
  if (!authorizedInvoiceId) {
    throw new Error(
      "Authorized invoice ID missing when attempting to fetch line items for invoice"
    );
  }

  const response = await invoicingProxy(ctx, `/v2/authorize/invoice/${authorizedInvoiceId}`, "GET");
  assertOk(response);
  return response.json();
};

export const getTransactionHistory = async (ctx, offset = 0, accountId) => {
  const queryAccountId = accountId ? `&account_id=${accountId}` : "";
  const response = await invoicingProxy(
    ctx,
    `/v1/transactions?statuses=processed,payout-failed&limit=${TRANSACTION_HISTORY_LIMIT}&offset=${offset}${queryAccountId}`,
    "GET"
  );
  assertOk(response);
  return response.json();
};

export const getPendingTransactionHistory = async (ctx, accountId) => {
  const queryAccountId = accountId ? `&account_id=${accountId}` : "";
  const response = await invoicingProxy(
    ctx,
    `/v1/transactions?statuses=pending${queryAccountId}`,
    "GET"
  );
  assertOk(response);
  return response.json();
};

export const getTransactionHistoryWithFilters = async (
  ctx,
  accountId,
  { types = [], statuses = [], offset, limit }
) => {
  const paginationParams =
    Number.isInteger(offset) && Number.isInteger(limit)
      ? {
          [transactionQueryParams.limit]: limit,
          [transactionQueryParams.offset]: offset,
        }
      : {};
  const queryParams = qs.stringify(
    {
      [transactionQueryParams.accountId]: accountId,
      [transactionQueryParams.statuses]: statuses,
      [transactionQueryParams.transactionTypes]: types,
      ...paginationParams,
    },
    { arrayFormat: "comma" }
  );

  const response = await invoicingProxy(ctx, `/v1/transactions?${queryParams}`, "GET");
  assertOk(response);
  return response.json();
};

export const getTransactionDetails = async (ctx, invoiceId) => {
  if (!invoiceId) {
    throw new Error("Invoice ID missing when attempting to fetch transaction details");
  }

  const response = await invoicingProxy(ctx, `/v1/transaction/${invoiceId}`, "GET");
  if (!response.ok) {
    const msg = await response.json();
    throw new Error(msg.payload?.error || "Unknown error occured");
  }
  return response.json();
};

export const sendTransactionReceiptEmail = async (ctx, invoiceId, email) => {
  if (!invoiceId || !email) {
    const missing = [];
    if (!invoiceId) missing.push("Invoice ID");
    if (!email) missing.push("Email");
    throw new Error(
      `Missing required data when attempting to send e-mail receipt: ${missing.toString()}`
    );
  }

  const response = await invoicingProxy(ctx, `/v1/transaction/${invoiceId}/receipt`, "POST", {
    email_address: email,
  });
  if (!response.ok) {
    const msg = await response.json();
    throw new Error(msg.payload?.error || "Unknown error occured");
  }
  return response.json();
};

export const makeGetDepositCalculations = (formatMessage) => {
  return async function getDepositCalculations(ctx, paymentMethodId, amount) {
    if (!paymentMethodId) {
      throw new Error(
        formatMessage({
          defaultMessage: "Payment method ID is missing when attempting to get deposit calculation",
          description: "Error message. 'Deposit calculation' means checking for sufficient funds.",
        })
      );
    }
    if (!amount) {
      throw new Error(
        formatMessage({
          defaultMessage: "Amount is empty when attempting to get deposit calculation",
          description:
            "Error message for empty input field. This field is for amount of money being deposited into a money account.",
        })
      );
    }

    const response = await invoicingProxy(
      ctx,
      `/v2/deposit/calculate?payment_method_id=${paymentMethodId}&amount=${amount}`,
      "GET"
    );

    if (!response.ok) {
      const msg = await response.json();
      throw new Error(msg.payload?.error || "Unknown error occured");
    }
    return response.json();
  };
};

export const intentDeposit = async (ctx, body) => {
  for (const [key, value] of Object.entries(body)) {
    if (!value) throw new Error(`${key} is missing when attempting to intent deposit`);
  }

  const response = await invoicingProxy(ctx, `/v2/deposit`, "POST", {
    account_id: body.accountId,
    deposit_provider: body.depositProvider,
    deposit_type: body.depositType,
    destination_wallet_id: body.destinationWalletId,
    request_amount: body.requestAmount,
    request_amount_fee: body.requestAmountFee,
    request_currency: body.requestCurrency,
    deposit_payment_method_id: body.depositPaymentMethodId,
  });

  if (!response.ok) {
    const msg = await response.json();
    throw new Error(msg.payload?.error || "Unknown error occured");
  }
  return response.json();
};

export const getPayoutCalculations = async (ctx, body) => {
  for (const [key, value] of Object.entries(body)) {
    if (!value) throw new Error(`${key} is missing when attempting to calculate payout fees`);
  }

  const response = await invoicingProxy(
    ctx,
    `/v2/payout/calculate-fees?currency=${body.currency}&amount=${body.amount}&payment_method_ids=${body.paymentMethodId}`,
    "GET"
  );

  if (!response.ok) {
    const msg = await response.json();
    throw new Error(msg.payload?.error || "Unknown error occured");
  }
  return response.json();
};

export const createPayoutRequest = async (ctx, body) => {
  for (const [key, value] of Object.entries(body)) {
    if (!value) throw new Error(`${key} is missing when attempting to create a payout request`);
  }

  const payload = { ...body };
  if (ctx.sessionId) {
    payload.session_id = ctx.sessionId
  }

  const response = await invoicingProxy(ctx, `/v2/payout`, "POST", payload);

  const data = await response.json();
  assertOk(response, [], data);
  return Promise.resolve(data);
};

export const getPendingPayouts = async (ctx) => {
  const response = await invoicingProxy(
    ctx,
    `/v1/transactions?statuses=pending&transaction_types=payout`,
    "GET"
  );
  if (!response.ok) {
    const msg = await response.json();
    throw new Error(msg.payload?.error || "Unknown error occured");
  }
  return response.json();
};

export const cancelPayoutRequest = async (ctx, { payoutId }) => {
  if (!payoutId) {
    throw new Error(`Payout ID is missing when attempting to cancel payout request`);
  }
  const response = await invoicingProxy(ctx, `/v2/payout/${payoutId}`, "DELETE");
  if (!response.ok) {
    const msg = await response.json();
    throw new Error(msg.payload?.error || "Unknown error occured");
  }
  return response.json();
};
